import type { AxiosRequestConfig } from 'axios';

import { AI_API_URL } from '~/lib/environment';
import { DEFAULT_MODEL, PLACEMENT_COMPONENTS } from '~/lib/ai/placementDefaults';
import { aiApi } from '~/modules/query/ai';
import { axiosBaseQuery } from '~/modules/query/base';
import type {
  DeletePlacementResponse,
  DuplicatePlacementRequest,
  DuplicatePlacementResponse,
  PlacementConfigDetail,
  PlacementConfigSummary,
  PlacementDraftInput,
  PlacementHistoryResponse,
  PlacementListResponse,
  PlacementTestResult,
  PlacementTestStatus,
  PlacementVersionEntry,
  RemovePlacementVersionRequest,
  RemovePlacementVersionResponse,
  RestorePlacementRequest,
  RestorePlacementResponse,
  RunPlacementTestRequest,
  RunPlacementTestResponse,
  SavePlacementDraftResponse,
  UpdatePlacementDraftRequest,
} from '~/modules/query/ai/types';

const seg = encodeURIComponent;
const BASE = '/manager/placements-configs';

const placementBaseQuery = axiosBaseQuery({ baseUrl: AI_API_URL });

interface PlacementQueryError {
  status?: number;
  data?: unknown;
  message: string;
}

type PlacementQueryResult<T> = { data: T } | { error: PlacementQueryError };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;
const asOptionalNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;
const asIsoDate = (value: unknown): string =>
  typeof value === 'string' ? value : new Date().toISOString();
const asOptionalIsoDate = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asDisplayName = (value: unknown): string | undefined => {
  const normalised = asString(value)?.trim();
  return normalised ? normalised : undefined;
};

const asWorkflowStatus = (value: unknown): PlacementConfigSummary['status'] =>
  value === 'active' || value === 'published' ? 'active' : 'draft';

const asTestStatus = (value: unknown): PlacementTestStatus | undefined =>
  value === 'passed' || value === 'failed' ? value : undefined;

function buildRenderSnippet(
  componentId: string,
  componentVersion: string,
  placementKey: string,
): string {
  const version = PLACEMENT_COMPONENTS.find(
    (component) => component.id === componentId,
  )?.versions.find((entry) => entry.version === componentVersion);

  if (!version) {
    throw new Error(
      `The AI service returned an unknown component version (${componentId} ${componentVersion}).`,
    );
  }

  return version.renderSnippet
    .replace(/\{\{placementKey\}\}/g, placementKey)
    .replace(/\{\{componentVersion\}\}/g, componentVersion);
}

function asActor(payload: unknown, fallbackId: unknown): PlacementConfigDetail['draftUpdatedBy'] {
  const record = isRecord(payload) ? payload : {};

  return {
    id: asString(record.id) || asString(fallbackId) || 'unknown-user',
    displayName: asDisplayName(record.displayName) || null,
  };
}

function mapSummary(payload: unknown): PlacementConfigSummary {
  if (!isRecord(payload)) {
    throw new Error('The AI service returned an invalid placement.');
  }

  const placementKey = asString(payload.placementKey);
  const componentId = asString(payload.componentId);
  const componentVersion = asString(payload.componentVersion);

  if (!(placementKey && componentId && componentVersion)) {
    throw new Error('The AI service returned a placement without a key, component or version.');
  }

  const createdByUser = isRecord(payload.createdByUser) ? payload.createdByUser : null;
  const updatedByUser = isRecord(payload.updatedByUser) ? payload.updatedByUser : null;

  return {
    id: asString(payload.id),
    placementKey,
    componentId,
    componentVersion,
    createdAt: asOptionalIsoDate(payload.createdAt),
    createdByName: asDisplayName(createdByUser?.displayName) || asDisplayName(payload.createdBy),
    description: asString(payload.description),
    model: asString(payload.model) || asString(payload.draftModel) || DEFAULT_MODEL,
    name: asString(payload.name),
    status: asWorkflowStatus(payload.status),
    updatedAt: asIsoDate(payload.updatedAt),
    updatedByName: asDisplayName(updatedByUser?.displayName) || asDisplayName(payload.updatedBy),
    lastTestAt: asOptionalIsoDate(payload.lastTestAt) || asOptionalIsoDate(payload.lastTestedAt),
    lastTestStatus: asTestStatus(payload.lastTestStatus),
  };
}

function mapDetail(payload: unknown): PlacementConfigDetail {
  if (!isRecord(payload)) {
    throw new Error('The AI service returned an invalid placement.');
  }

  const summary = mapSummary(payload);
  const promptTemplate = asString(payload.promptTemplate) || asString(payload.draftPromptTemplate);
  const model = asString(payload.model) || asString(payload.draftModel);

  if (!(promptTemplate && model)) {
    throw new Error('The AI service returned a placement without a prompt template or model.');
  }

  // Resolves the catalog entry so an unknown component/version fails here rather
  // than rendering a snippet the caller cannot use.
  buildRenderSnippet(summary.componentId, summary.componentVersion, summary.placementKey);

  return {
    ...summary,
    promptTemplate,
    model,
    latestChangeNote: asString(payload.changeNote) || '',
    draftUpdatedBy: asActor(payload.updatedByUser ?? payload.updatedBy, payload.updatedBy),
  };
}

function mapHistoryEntry(payload: unknown): PlacementVersionEntry {
  if (!isRecord(payload)) {
    throw new Error('The AI service returned an invalid placement history entry.');
  }

  const status = asWorkflowStatus(payload.status);
  const changeNote = asString(payload.changeNote) || '';
  const componentId = asString(payload.componentId);
  const componentVersion = asString(payload.componentVersion);

  if (!(componentId && componentVersion)) {
    throw new Error('The AI service returned a history entry without a component or version.');
  }

  return {
    id: asString(payload.id) || `version-${asOptionalNumber(payload.versionNumber) ?? 0}`,
    placementKey: asString(payload.placementKey) || 'unknown.placement',
    versionNumber: asOptionalNumber(payload.versionNumber) || 1,
    action: changeNote.toLowerCase().includes('restore')
      ? 'restored'
      : status === 'active'
        ? 'activated'
        : 'draft_saved',
    componentId,
    componentVersion,
    promptTemplate: asString(payload.promptTemplate) || '',
    model: asString(payload.model) || DEFAULT_MODEL,
    status,
    changeNote,
    createdAt: asIsoDate(payload.createdAt),
    changedBy: asActor(payload.changedByUser ?? payload.createdBy, payload.createdBy),
  };
}

function mapTestResult(payload: unknown): RunPlacementTestResponse {
  if (!(isRecord(payload) && isRecord(payload.result) && isRecord(payload.preview))) {
    throw new Error('The AI service returned an invalid placement test result.');
  }

  const { result, preview } = payload;
  const usage = isRecord(result.usage) ? result.usage : {};
  const placementKey = asString(result.placementKey) || 'unknown.placement';
  const componentId =
    asString(preview.componentId) || asString(result.componentId) || 'unknown.component';
  const componentVersion = asString(preview.componentVersion) || asString(result.componentVersion);

  if (!componentVersion) {
    throw new Error('The AI service returned a test result without component selection metadata.');
  }

  const output = asString(result.text) || '';
  const error = asString(result.error);

  const mapped: PlacementTestResult = {
    id: asString(result.id) || `test-${new Date().toISOString()}`,
    placementKey,
    status: error ? 'failed' : 'passed',
    createdAt: asIsoDate(result.createdAt),
    prompt: {
      compiled: asString(result.compiledPrompt) || '',
      output,
      reasoning: asString(result.reasoning),
    },
    preview: {
      componentId,
      componentVersion,
      props: isRecord(preview.sampleProps) ? preview.sampleProps : {},
      payload: {
        placementKey,
        componentId,
        componentVersion,
        sampleProps: isRecord(preview.sampleProps) ? preview.sampleProps : {},
      },
      snippet: buildRenderSnippet(componentId, componentVersion, placementKey),
    },
    usage: {
      promptTokens: asOptionalNumber(usage.promptTokens) || 0,
      completionTokens: asOptionalNumber(usage.completionTokens) || 0,
      totalTokens: asOptionalNumber(usage.totalTokens) || 0,
      durationMs: asOptionalNumber(usage.durationMs) || 0,
    },
    error,
  };

  return { result: mapped };
}

/** The saved placement from a create/update/restore/duplicate/version-delete response. */
function savedPlacement(payload: unknown, invalidMessage: string): SavePlacementDraftResponse {
  const record = isRecord(payload) ? payload : null;
  const config =
    record && isRecord(record.config)
      ? record.config
      : record && isRecord(record.placement)
        ? record.placement
        : null;

  if (!config) {
    throw new Error(invalidMessage);
  }

  return { success: true, placement: mapDetail(config) };
}

async function queryPlacement<T>(
  config: AxiosRequestConfig,
  map: (body: unknown) => T,
): Promise<PlacementQueryResult<T>> {
  const result = await placementBaseQuery(config);

  if (result.error) {
    return { error: result.error };
  }

  try {
    return { data: map(result.data) };
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'The AI service returned an unexpected placement response.';

    return {
      error: {
        status: 502,
        data: { error: message, code: 'INVALID_PLACEMENT_RESPONSE' },
        message,
      },
    };
  }
}

export const placementsApi = aiApi.injectEndpoints({
  endpoints: (builder) => ({
    listPlacements: builder.query<PlacementListResponse, void>({
      queryFn: () =>
        queryPlacement({ url: BASE, method: 'GET' }, (body): PlacementListResponse => {
          const response = isRecord(body) ? body : {};
          const configs = Array.isArray(response.configs)
            ? response.configs
            : Array.isArray(response.placements)
              ? response.placements
              : null;

          if (!configs) {
            throw new Error('The AI service returned an invalid placement list.');
          }

          return { placements: configs.map(mapSummary) };
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.placements.map((placement) => ({
                type: 'AiPlacement' as const,
                id: placement.placementKey,
              })),
              { type: 'AiPlacement' as const, id: 'LIST' },
            ]
          : [{ type: 'AiPlacement' as const, id: 'LIST' }],
    }),
    getPlacement: builder.query<{ placement: PlacementConfigDetail }, string>({
      queryFn: (placementKey) =>
        queryPlacement({ url: `${BASE}/${seg(placementKey)}`, method: 'GET' }, (body) => {
          const response = isRecord(body) ? body : null;
          const config =
            response && isRecord(response.config)
              ? response.config
              : response && isRecord(response.placement)
                ? response.placement
                : null;

          if (!config) {
            throw new Error('The AI service returned an invalid placement.');
          }

          const placement = mapDetail(config);
          const latestTest =
            response && Array.isArray(response.recentTestRuns)
              ? response.recentTestRuns[0]
              : undefined;

          if (isRecord(latestTest)) {
            if (typeof latestTest.success === 'boolean') {
              placement.lastTestStatus = latestTest.success ? 'passed' : 'failed';
            }
            placement.lastTestAt = asOptionalIsoDate(latestTest.createdAt) || placement.lastTestAt;
          }

          return { placement };
        }),
      providesTags: (_result, _error, placementKey) => [{ type: 'AiPlacement', id: placementKey }],
    }),
    createPlacement: builder.mutation<SavePlacementDraftResponse, PlacementDraftInput>({
      queryFn: (data) =>
        queryPlacement({ url: BASE, method: 'POST', data }, (body) =>
          savedPlacement(body, 'The AI service returned an invalid create placement response.'),
        ),
      invalidatesTags: [{ type: 'AiPlacement', id: 'LIST' }],
    }),
    updatePlacementDraft: builder.mutation<SavePlacementDraftResponse, UpdatePlacementDraftRequest>(
      {
        queryFn: ({ placementKey, updates }) =>
          queryPlacement(
            { url: `${BASE}/${seg(placementKey)}/draft`, method: 'PATCH', data: updates },
            (body) =>
              savedPlacement(body, 'The AI service returned an invalid update placement response.'),
          ),
        invalidatesTags: (_result, _error, arg) => [
          { type: 'AiPlacement', id: 'LIST' },
          { type: 'AiPlacement', id: arg.placementKey },
          { type: 'AiPlacementHistory', id: arg.placementKey },
        ],
      },
    ),
    runPlacementTest: builder.mutation<RunPlacementTestResponse, RunPlacementTestRequest>({
      queryFn: ({ placementKey, sampleContext, sampleProps, user, userInstructions }) =>
        queryPlacement(
          {
            url: `${BASE}/${seg(placementKey)}/test`,
            method: 'POST',
            data: { context: sampleContext, sampleProps, user, userInstructions },
          },
          mapTestResult,
        ),
      invalidatesTags: (_result, _error, arg) => [{ type: 'AiPlacement', id: arg.placementKey }],
    }),
    restorePlacement: builder.mutation<RestorePlacementResponse, RestorePlacementRequest>({
      queryFn: ({ placementKey, versionNumber, changeNote }) =>
        queryPlacement(
          {
            url: `${BASE}/${seg(placementKey)}/restore`,
            method: 'POST',
            data: { versionNumber, changeNote },
          },
          (body) =>
            savedPlacement(
              body,
              'The AI service returned an invalid restore placement response.',
            ) as RestorePlacementResponse,
        ),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiPlacement', id: 'LIST' },
        { type: 'AiPlacement', id: arg.placementKey },
        { type: 'AiPlacementHistory', id: arg.placementKey },
      ],
    }),
    duplicatePlacement: builder.mutation<DuplicatePlacementResponse, DuplicatePlacementRequest>({
      queryFn: ({ placementKey, nextPlacementKey }) =>
        queryPlacement(
          {
            url: `${BASE}/${seg(placementKey)}/duplicate`,
            method: 'POST',
            data: { nextPlacementKey },
          },
          (body) =>
            savedPlacement(
              body,
              'The AI service returned an invalid duplicate placement response.',
            ) as DuplicatePlacementResponse,
        ),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiPlacement', id: 'LIST' },
        { type: 'AiPlacement', id: arg.placementKey },
      ],
    }),
    deletePlacement: builder.mutation<DeletePlacementResponse, string>({
      queryFn: (placementKey) =>
        queryPlacement(
          { url: `${BASE}/${seg(placementKey)}`, method: 'DELETE' },
          (body): DeletePlacementResponse => {
            if (!isRecord(body) || body.success !== true) {
              throw new Error('The AI service returned an invalid delete placement response.');
            }

            return {
              success: true,
              deleted: true,
              placementKey: asString(body.placementKey) || placementKey,
            };
          },
        ),
      invalidatesTags: (_result, _error, placementKey) => [
        { type: 'AiPlacement', id: 'LIST' },
        { type: 'AiPlacement', id: placementKey },
        { type: 'AiPlacementHistory', id: placementKey },
      ],
    }),
    deletePlacementVersion: builder.mutation<
      RemovePlacementVersionResponse,
      RemovePlacementVersionRequest
    >({
      queryFn: ({ placementKey, versionNumber }) =>
        queryPlacement(
          { url: `${BASE}/${seg(placementKey)}/history/${versionNumber}`, method: 'DELETE' },
          (body) =>
            savedPlacement(
              body,
              'The AI service returned an invalid remove version response.',
            ) as RemovePlacementVersionResponse,
        ),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiPlacement', id: 'LIST' },
        { type: 'AiPlacement', id: arg.placementKey },
        { type: 'AiPlacementHistory', id: arg.placementKey },
      ],
    }),
    getPlacementHistory: builder.query<
      PlacementHistoryResponse,
      { placementKey: string; limit?: number }
    >({
      queryFn: ({ placementKey, limit = 20 }) =>
        queryPlacement(
          { url: `${BASE}/${seg(placementKey)}/history?limit=${limit}`, method: 'GET' },
          (body): PlacementHistoryResponse => {
            if (!(isRecord(body) && Array.isArray(body.history))) {
              throw new Error('The AI service returned an invalid placement history.');
            }

            return { history: body.history.map(mapHistoryEntry) };
          },
        ),
      providesTags: (_result, _error, arg) => [
        { type: 'AiPlacementHistory', id: arg.placementKey },
      ],
    }),
  }),
});

export const {
  useListPlacementsQuery,
  useGetPlacementQuery,
  useCreatePlacementMutation,
  useUpdatePlacementDraftMutation,
  useRunPlacementTestMutation,
  useRestorePlacementMutation,
  useDuplicatePlacementMutation,
  useDeletePlacementMutation,
  useDeletePlacementVersionMutation,
  useGetPlacementHistoryQuery,
} = placementsApi;
